import React, { useMemo } from 'react';

import dayjs from 'dayjs';
import { Alert, Flex, Table, Tag, type TableColumnsType } from 'antd';
import { Severity } from 'src/interfaces/entities/severity';
import { Occurrence } from 'src/interfaces/entities/occurrence';
import useOccurrences from 'src/pages/dashboard/contexts/useOccurrences';
import {
	SeverityColor
} from 'src/pages/dashboard/components/OccurrencesTable/styles';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';

export default function OccurrencesTable() {
	const navigate = useNavigate();
	const { occurrences, isLoading, } = useOccurrences();

	const uniqueTags = useMemo(() => {
		const tags: string[] = [];
		occurrences.forEach(occ => {
			occ.observer.tags.forEach(tag => {
				if (!tags.includes(tag)) tags.push(tag);
			});
		});
		return tags;
	}, [ occurrences, ]);

	const columns = useMemo<TableColumnsType<Occurrence>>(() => [
		{
			dataIndex: [ 'observer', 'name', ],
			title: 'Observador disparado',
			key: 'observer_name',
			render: (name: string) => (
				<Flex gap={8} align='center'>{name}</Flex>
			),
		},
		{
			dataIndex: [ 'observer', 'severity', ],
			title: 'Severidade',
			key: 'severity',
			render: (severity: Severity) => (
				<Flex gap={8} align='center'>
					<SeverityColor style={{ backgroundColor: severity?.color, }} />
					{severity?.name}
				</Flex>
			),
		},
		{
			dataIndex: 'creation_date',
			title: 'Data/hora',
			key: 'creation_date',
			sorter: (a, b) => new Date(a.creation_date).getTime() - new Date(b.creation_date).getTime(),
			render: (value: string) => dayjs(value).format('DD/MM/YY, HH:mm'),
		},
		{
			dataIndex: [ 'observer', 'tags', ],
			title: 'Tag(s)',
			key: 'tags',
			filters: uniqueTags.map(tag => ({ text: tag, value: tag, })),
			onFilter: (value, record) => record.observer.tags.includes(value as string),
			render: (tags: string[]) => (
				<>
					{tags.map(tag => (
						<Tag key={tag} color='geekblue'>{tag}</Tag>
					))}
				</>
			),
		},
		{
			title: 'Status',
			key: 'status',
			render: (_value, record) => {
				const hasRecovery = _.findLast(occurrences, occ =>
					occ.observer_id === record.observer_id && occ.is_recovery &&
new Date(occ.creation_date) > new Date(record.creation_date)
				);
				return <Tag color={hasRecovery ? 'green' : 'gold'}>{hasRecovery ? 'Resolvido' : 'Aberto'}</Tag>;
			},
		},
	], [ occurrences, uniqueTags, ]);

	const tableData = useMemo(
		() => occurrences.filter(occ => !occ.is_recovery),
		[ occurrences, ]
	);

	return (
		<Table<Occurrence>
			columns={columns}
			dataSource={tableData}
			rowKey="ocurrence_id"
			loading={isLoading}
			size="small"
			pagination={false}
			scroll={{ y: 468, }}
			onRow={(record) => ({
				onClick: () => navigate(`/occurrence/${record.ocurrence_id}`),
				style: { cursor: 'pointer', },
			})}
			expandable={{
				expandedRowRender: (record) => {
					const hasRecovery = _.findLast(occurrences, occ =>
						occ.observer_id === record.observer_id && occ.is_recovery &&
new Date(occ.creation_date) > new Date(record.creation_date)
					);

					return (
						<Flex vertical gap={8}>
							{hasRecovery && (
								<Alert
									message={`Esta ocorrência foi recuperada na data: ${hasRecovery.creation_date}`}
									type='success'
								/>
							)}
							{record.preview_message}
						</Flex>
					);
				},
			}}
		/>
	);
}
